import type { Program } from "./schemas/program.js";
import type { Exercise } from "./schemas/exercise.js";
import type { PlannedSet } from "./schemas/plan.js";
import { EFFORT_HARD_SET_RIR } from "./effortScale.js";
import { resolveFullCycleSessions, type ResolvedSession } from "./programResolution.js";
import { catalogBySlug, resolveExerciseMuscleCredits } from "./volumeByMuscle.js";
import { HARD_SET_MUSCLE_CONTRIB_MIN } from "./weeklyTargetActual.js";

// A prescribed set counts as a HARD set — the SAME thing hardSetsByMuscle counts
// in the logged actuals — unless the program explicitly targets it submaximal.
// The actual counts a logged set when its effective RIR ≤ EFFORT_HARD_SET_RIR
// (≈ RPE ≥ 7); mirror that on the prescription's target effort. Most program
// sets carry no explicit RPE/RIR target (percent-of-max / absolute working
// sets) — those ARE the working sets, so "no target" = assumed hard, and only a
// deliberately easy target (RIR ≥ 4 / RPE ≤ 6 — the meter's bottom `4+`
// step) drops out. Without this filter
// the readout would count ramp/back-off/technique volume the actual never
// credits, systematically overstating the program's ask.
// Review fix (PE-22 K3 triad, Warning consolidation): the SAME three-way rule
// once lived twice — here against a resolved PlannedSet's targetRir/targetRpe,
// and again in hardSetsByMuscleFromDraft.ts against the Draft's per-row
// intensityKind + per-week cell value. Both sites now call this pure decision;
// each maps its own shape down to "the two numbers a resolved set would
// carry" and nothing else changes.
//
// #355: an AMRAP stop rule is an effort target too, read only when the set
// carries no rpe/rir of its own (an explicit one wins): `amrapStopRir`.
export const isPrescribedHardSetByEffort = (
  targetRir: number | null | undefined,
  targetRpe: number | null | undefined,
  amrapStopRir?: number | null,
): boolean => {
  if (targetRir != null) return targetRir <= EFFORT_HARD_SET_RIR;
  if (targetRpe != null) return targetRpe >= 10 - EFFORT_HARD_SET_RIR;
  if (amrapStopRir != null) return amrapStopRir <= EFFORT_HARD_SET_RIR;
  return true; // no effort target at all → a working set, assumed hard
};

/** The RIR an AMRAP set's stop rule stands for: technical failure is RIR 0.
 *  null for a set that is not AMRAP (the resolver ignores a stop there) or
 *  has no rule. */
export const amrapStopRirOf = (
  s: Pick<PlannedSet, "amrap" | "amrapStop">,
): number | null => {
  if (s.amrap !== true || s.amrapStop === undefined) return null;
  return s.amrapStop.kind === "rir" ? s.amrapStop.rir : 0;
};

export const isPrescribedHardSet = (s: PlannedSet): boolean =>
  isPrescribedHardSetByEffort(s.targetRir, s.targetRpe, amrapStopRirOf(s));

// AP-7 (2026-07-07). What the PROGRAM prescribes per muscle each week — the
// planning-side counterpart to hardSetsByMuscle's logged actuals. Grounds the
// target editor ("the program prescribes ~11/wk" next to "you average 10.4")
// and lets the meter tell "no coverage" (the program trains this muscle 0×)
// apart from "building 0/12" (prescribed, not yet trained this week).
//
// Walks the FULL macrocycle via resolveFullCycleSessions, so alternating /
// phase-offset variants resolve to the exercises actually trained each week,
// counts each resolved entry's HARD planned sets (isPrescribedHardSet — the
// same effort boundary the actual uses), and attributes them with the SAME
// piecewise credit the actuals use (resolveExerciseMuscleCredits, AP-6) — so
// "prescribes N" and the meter's actual are the same kind of number and compare
// like-for-like. Averaged over cycleWeeks for the per-week figure.
//
// Programs don't model warm-ups (no schema flag — warm-ups are done in-session,
// never prescribed); the effort filter, not a warm-up flag, keeps easy volume
// out. Catalog misses (custom or untagged rows) contribute nothing, same as the
// volume math.
// HARD prescribed sets for ONE resolved day, per exercise slug — the per-session
// primitive both the weekly average (below) and the per-day PROJECTION (AP-10,
// programTargetProjection) build on, so "what the program prescribes" is counted
// in exactly one place. A hard set is weight-independent (it's a set count), so
// this counts directly, never through the volume-gated session scorer.
// `assumed` (#355): the hard sets with no rpe/rir target, hard by assumption.
export type HardSetCount = { hard: number; assumed: number };

export const prescribedHardSetCounts = (
  session: ResolvedSession,
): Record<string, HardSetCount> => {
  const out: Record<string, HardSetCount> = {};
  for (const entry of session.entries) {
    let hard = 0;
    let assumed = 0;
    for (const s of entry.sets) {
      if (!isPrescribedHardSet(s)) continue;
      hard += 1;
      if (s.targetRir == null && s.targetRpe == null && amrapStopRirOf(s) === null) assumed += 1;
    }
    if (hard === 0) continue;
    const prev = out[entry.exerciseId] ?? { hard: 0, assumed: 0 };
    out[entry.exerciseId] = { hard: prev.hard + hard, assumed: prev.assumed + assumed };
  }
  return out;
};

export const prescribedHardSetsByExercise = (
  session: ResolvedSession,
): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [slug, c] of Object.entries(prescribedHardSetCounts(session))) {
    out[slug] = c.hard;
  }
  return out;
};

/** One day of one round: its hard sets per exercise. The common input of
 *  the saved program (below) and the editor's Draft (hardSetsByMuscleFromDraft). */
export type RoundHardSets = {
  weekIdx: number;
  /** `assumed` is left out by the Draft, which nobody reads it from. */
  byExercise: Record<string, { hard: number; assumed?: number }>;
};

export const roundHardSetsOf = (
  sessions: ReadonlyArray<ResolvedSession>,
): RoundHardSets[] =>
  sessions.map((session) => ({
    weekIdx: session.weekIdx,
    byExercise: prescribedHardSetCounts(session),
  }));

export type HardSetsByWeek = {
  /** [weekIdx] → muscle → hard sets, with the actuals' credit (AP-6). */
  hard: Record<string, number>[];
  /** The same, counting only the sets assumed hard. */
  assumed: Record<string, number>[];
};

/** Hard sets per muscle in each round of the cycle. The one walk behind the
 *  cycle average and the simulator's weeks (#355). Catalog misses (custom or
 *  untagged rows) contribute nothing, same as the volume math. */
export const hardSetsByMuscleByWeek = (
  rounds: Iterable<RoundHardSets>,
  cycleWeeks: number,
  catalog: ReadonlyArray<Exercise>,
): HardSetsByWeek => {
  const bySlug = catalogBySlug(catalog);
  const creditsOf = new Map<string, ReadonlyArray<readonly [string, number]>>();
  const hard: Record<string, number>[] = Array.from({ length: cycleWeeks }, () => ({}));
  const assumed: Record<string, number>[] = Array.from({ length: cycleWeeks }, () => ({}));
  for (const { weekIdx, byExercise } of rounds) {
    const h = hard[weekIdx];
    const a = assumed[weekIdx];
    if (!h || !a) continue;
    for (const [slug, c] of Object.entries(byExercise)) {
      let credits = creditsOf.get(slug);
      if (!credits) {
        const ex = bySlug.get(slug);
        credits = ex ? [...resolveExerciseMuscleCredits(ex, HARD_SET_MUSCLE_CONTRIB_MIN)] : [];
        creditsOf.set(slug, credits);
      }
      for (const [m, credit] of credits) {
        if (c.hard > 0) h[m] = (h[m] ?? 0) + c.hard * credit;
        if ((c.assumed ?? 0) > 0) a[m] = (a[m] ?? 0) + c.assumed! * credit;
      }
    }
  }
  return { hard, assumed };
};

/** The full-cycle average of per-week maps (Antenore 2026-08-27: the sum
 *  over the cycle divided by its weeks). */
export const cycleAverageByMuscle = (
  weeks: ReadonlyArray<Record<string, number>>,
): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const week of weeks) {
    for (const [m, n] of Object.entries(week)) out[m] = (out[m] ?? 0) + n;
  }
  for (const m of Object.keys(out)) out[m] = (out[m] ?? 0) / weeks.length;
  return out;
};

export const programWeeklyPrescription = (
  program: Program,
  catalog: ReadonlyArray<Exercise>,
): Record<string, number> => {
  const cycleWeeks = program.cycleWeeks;
  if (!Number.isInteger(cycleWeeks) || cycleWeeks < 1) return {};
  return cycleAverageByMuscle(
    hardSetsByMuscleByWeek(roundHardSetsOf(resolveFullCycleSessions(program)), cycleWeeks, catalog)
      .hard,
  );
};
